/**
 * The Messages store: one per signed-in user and side (the salon's, or a
 * client's), shared by the page, the header icon and the nav counts, so
 * none of them needs a provider.
 *
 * It mirrors the adapter (api.js) and adds what makes chat feel instant:
 * a sent message shows straight away as 'sending' and is swapped for the
 * server's copy when it lands (or marked 'failed' with Try again); reactions
 * flip before the server answers and flip back if it says no.
 *
 * Logout does not reload the app (see memory: logout no reload), so the
 * store checks who is signed in on every use and starts again when it
 * changes.
 */
import { createContext, useContext, useEffect, useReducer, useRef } from 'react';
import { createAdapter, identityKey } from './api';
import {
    localId, toggleReaction, unreadCount, sortConversations, findMentions, lastShown, needsYou,
} from './model';

/**
 * One store per side of the conversation. The salon app uses the default
 * (below); the client app makes its own with the client adapter, so a salon
 * owner who is also someone's client never mixes the two.
 */
export function createMessagesStore({ makeAdapter, keyOf }) {
    const TYPING_TTL = 6000;

    const empty = () => ({
        status: 'idle', error: null, me: null, people: {}, conversations: {}, messages: {}, typing: {},
        reminders: [], saved: [], tickets: [], salons: {}, channelOrder: [], quickReplies: [], prefs: { pausedUntil: null, workingOnly: true }, clientSettings: { awayOn: false, awayText: '' }, version: 0,
    });

    let state = empty();
    let unreadHold = null; // a chat just marked unread: don't read it again until it's reopened
    let adapter = null;
    let adapterKey = null;
    let unsubscribe = null;
    const listeners = new Set();
    const noticeListeners = new Set();
    const typingTimers = {};

    /** One-line notes for the toast ("Reminder: ..."), from anywhere in the store. */
    function onNotice(fn) {
        noticeListeners.add(fn);
        return () => noticeListeners.delete(fn);
    }
    function notice(text) { noticeListeners.forEach((fn) => fn(text)); }

    /**
     * Someone else's new message, as it lands (the in-app banner and, later,
     * browser notifications listen). The chat on screen (setActive) is passed
     * so a listener can stay quiet about the chat you're already reading.
     */
    const incomingListeners = new Set();
    let activeId = null;
    function onIncoming(fn) {
        incomingListeners.add(fn);
        return () => incomingListeners.delete(fn);
    }
    function announce(m) {
        const me = state.me && state.me.id;
        if (!m || !me || m.authorId === me || m.system || m.auto || m.note || m.deletedAt) return;
        if (m.deliverAt && new Date(m.deliverAt) > new Date()) return;
        const known = (state.messages[m.conversationId] || []).some((x) => x.id === m.id);
        if (known) return; // an update to one we've seen, not a new one
        incomingListeners.forEach((fn) => fn(m, activeId));
    }

    function setState(updater) {
        const next = updater(state);
        if (next === state) return;
        state = { ...next, version: state.version + 1 };
        listeners.forEach((fn) => fn());
    }

    function patchMessages(conversationId, fn) {
        setState((s) => ({ ...s, messages: { ...s.messages, [conversationId]: fn(s.messages[conversationId] || []) } }));
    }

    function upsertMessage(message) {
        patchMessages(message.conversationId, (list) => {
            const i = list.findIndex((m) => m.id === message.id);
            if (i === -1) return list.concat(message);
            const copy = list.slice();
            copy[i] = message;
            return copy;
        });
    }

    function setConversation(conversation) {
        setState((s) => ({ ...s, conversations: { ...s.conversations, [conversation.id]: { ...(s.conversations[conversation.id] || {}), ...conversation } } }));
    }

    function setTyping(conversationId, userId, on) {
        const key = `${conversationId}:${userId}`;
        clearTimeout(typingTimers[key]);
        setState((s) => {
            const now = { ...(s.typing[conversationId] || {}) };
            if (on) now[userId] = true; else delete now[userId];
            return { ...s, typing: { ...s.typing, [conversationId]: now } };
        });
        if (on) typingTimers[key] = setTimeout(() => setTyping(conversationId, userId, false), TYPING_TTL);
    }

    function onEvent(e) {
        if (e.type === 'message') {
            setTyping(e.message.conversationId, e.message.authorId, false);
            announce(e.message);
            upsertMessage(e.message);
        } else if (e.type === 'message-updated') {
            upsertMessage(e.message);
        } else if (e.type === 'conversation') {
            setConversation(e.conversation);
        } else if (e.type === 'read') {
            setState((s) => {
                const c = s.conversations[e.conversationId];
                if (!c) return s;
                return { ...s, conversations: { ...s.conversations, [c.id]: { ...c, readUpTo: { ...c.readUpTo, [e.userId]: e.at } } } };
            });
        } else if (e.type === 'typing') {
            setTyping(e.conversationId, e.userId, e.on);
        } else if (e.type === 'reminder') {
            setState((s) => ({ ...s, reminders: s.reminders.map((r) => (r.id === e.reminder.id ? e.reminder : r)) }));
            const m = (state.messages[e.reminder.conversationId] || []).find((x) => x.id === e.reminder.messageId);
            notice(`Reminder: ${m && m.text ? m.text.slice(0, 60) : 'a message you saved'}`);
        } else if (e.type === 'tickets') {
            setState((s) => ({ ...s, tickets: e.tickets }));
        } else if (e.type === 'salons') {
            setState((s) => ({ ...s, salons: e.salons }));
        } else if (e.type === 'people') {
            setState((s) => ({ ...s, people: e.people }));
        } else if (e.type === 'reset') {
            boot(true);
        }
    }

    // Settles when the latest bootstrap has landed. Calls that create
    // something (a new chat) wait for it, or the bootstrap's older snapshot
    // would land on top of what they made.
    let loaded = Promise.resolve();

    function boot(force) {
        if (!adapter) return;
        setState((s) => ({ ...s, status: s.status === 'ready' && !force ? 'ready' : 'loading' }));
        loaded = adapter.bootstrap()
            .then((data) => setState((s) => ({ ...s, status: 'ready', error: null, ...data, typing: {} })))
            .catch((err) => setState((s) => ({ ...s, status: 'error', error: (err && err.message) || 'Could not load messages' })));
    }

    /** Start (or restart, after a sign-in change) and return the adapter. */
    function ensureStarted() {
        const key = keyOf();
        if (adapter && key === adapterKey) return adapter;
        if (unsubscribe) unsubscribe();
        if (adapter) adapter.dispose();
        state = empty();
        adapter = makeAdapter();
        adapterKey = key;
        unsubscribe = adapter.subscribe(onEvent);
        boot();
        return adapter;
    }

    function getState() { return state; }

    function subscribe(fn) {
        listeners.add(fn);
        return () => listeners.delete(fn);
    }

    /**
     * Read from the store and re-render when that slice changes. The selector
     * should return values straight from the state (or primitives) so the
     * equality check stays cheap.
     */
    function useMessagesState(selector, isEqual = Object.is) {
        ensureStarted();
        const [, rerender] = useReducer((n) => n + 1, 0);
        // Read on every render, so a selector that depends on props (the open
        // chat's id) is never a render behind.
        const value = selector(state);
        const sel = useRef(selector);
        const latest = useRef(value);
        sel.current = selector;
        latest.current = value;
        useEffect(() => {
            const check = () => { if (!isEqual(sel.current(state), latest.current)) rerender(); };
            check();
            return subscribe(check);
        }, []); // eslint-disable-line react-hooks/exhaustive-deps
        return value;
    }

    /* ------------------------------------------------------------- selectors */

    const inboxCache = { version: -1, rows: {} };

    /** Conversations in a space, sorted, each with its last message and unread count. */
    function selectInbox(s, space) {
        if (inboxCache.version !== s.version) { inboxCache.version = s.version; inboxCache.rows = {}; }
        if (inboxCache.rows[space]) return inboxCache.rows[space];
        const meId = s.me && s.me.id;
        // Only chats I'm in (a channel I left drops off my list)
        const list = Object.keys(s.conversations).map((id) => s.conversations[id])
            .filter((c) => c.space === space && (!meId || !c.memberIds || c.memberIds.indexOf(meId) !== -1));
        const rows = sortConversations(list).map((c) => {
            const all = s.messages[c.id] || [];
            // Bailey handed it to the team: counts as unread until someone answers
            const unread = c.doneAt ? 0 : Math.max(unreadCount(c, all, meId), needsYou(c) ? 1 : 0);
            return { conversation: c, last: lastShown(all), unread, typing: Object.keys(s.typing[c.id] || {}) };
        });
        inboxCache.rows[space] = rows;
        return rows;
    }

    /** Unread across a space (muted chats count only when someone mentions me). */
    function selectSpaceUnread(s, space) {
        const meId = s.me && s.me.id;
        return Object.keys(s.conversations).reduce((sum, id) => {
            const c = s.conversations[id];
            if (c.space !== space || c.doneAt) return sum;
            const all = s.messages[id] || [];
            if (!c.muted) return sum + Math.max(unreadCount(c, all, meId), needsYou(c) ? 1 : 0);
            const mark = (c.readUpTo && c.readUpTo[meId]) || '';
            return sum + all.filter((m) => m.createdAt > mark && (m.mentions || []).indexOf(meId) !== -1).length;
        }, 0);
    }

    /** Unread across every space this person can see (data/spaces.js); all three by default. */
    function selectTotalUnread(s, spaces) {
        return (spaces || ['team', 'clients', 'support']).reduce((sum, sp) => sum + selectSpaceUnread(s, sp), 0);
    }

    /* --------------------------------------------------------------- actions */

    const actions = {
        /** Optimistic send: on screen now, swapped for the server copy when it lands. */
        send(conversationId, draft) {
            const a = ensureStarted();
            const me = state.me;
            if (!me) return null;
            const text = (draft.text || '').trim();
            if (!text && !(draft.attachments && draft.attachments.length) && !draft.card) return null;
            const temp = {
                id: localId('local'), conversationId, authorId: me.id, createdAt: new Date().toISOString(),
                text, attachments: draft.attachments || [], card: draft.card || null,
                replyToId: draft.replyToId || null, threadRootId: draft.threadRootId || null,
                reactions: {}, mentions: findMentions(text, state.people), status: 'sending', fresh: true,
                note: !!draft.note, deliverAt: draft.deliverAt || null,
                context: draft.context && draft.context.length ? draft.context : undefined,
                forwardedFrom: draft.forwardedFrom || undefined,
                channel: draft.channel || undefined, // client chats: app, sms or email
            };
            upsertMessage(temp);
            const c = state.conversations[conversationId];
            if (c && !temp.threadRootId && !temp.note) setConversation({ ...c, lastMessageAt: temp.createdAt, readUpTo: { ...c.readUpTo, [me.id]: temp.createdAt } });
            a.send(conversationId, { ...draft, text })
                .then((saved) => patchMessages(conversationId, (list) => {
                    // The socket may already have delivered it; keep one copy, where the temp sat.
                    const without = list.filter((m) => m.id !== saved.id);
                    return without.map((m) => (m.id === temp.id ? { ...saved, fresh: true } : m));
                }))
                // A refused text says why (stopped, balance-paused, not-allowed, carrier)
                .catch((e) => patchMessages(conversationId, (list) => list.map((m) => (m.id === temp.id ? { ...m, status: 'failed', failedReason: (e && e.reason) || null } : m))));
            return temp.id;
        },

        /** Send a failed message again; channel moves it (a refused text, by email). */
        retry(conversationId, messageId, channel) {
            const failed = (state.messages[conversationId] || []).find((m) => m.id === messageId);
            if (!failed) return;
            patchMessages(conversationId, (list) => list.filter((m) => m.id !== messageId));
            actions.send(conversationId, { text: failed.text, attachments: failed.attachments, card: failed.card, replyToId: failed.replyToId, threadRootId: failed.threadRootId, channel: channel || failed.channel });
        },

        discard(conversationId, messageId) {
            patchMessages(conversationId, (list) => list.filter((m) => m.id !== messageId));
        },

        react(conversationId, messageId, emoji) {
            const a = ensureStarted();
            const me = state.me;
            const before = (state.messages[conversationId] || []).find((m) => m.id === messageId);
            if (!me || !before || before.status === 'sending') return;
            upsertMessage({ ...before, reactions: toggleReaction(before.reactions, emoji, me.id) });
            a.react(conversationId, messageId, emoji).catch(() => upsertMessage(before));
        },

        edit(conversationId, messageId, text) {
            const a = ensureStarted();
            const before = (state.messages[conversationId] || []).find((m) => m.id === messageId);
            const clean = (text || '').trim();
            if (!before || !clean || clean === before.text) return;
            upsertMessage({ ...before, text: clean, editedAt: new Date().toISOString() });
            a.edit(conversationId, messageId, clean).catch(() => upsertMessage(before));
        },

        /** opts.moderate: a manager removing someone else's message (data/roles.js). */
        remove(conversationId, messageId, opts) {
            const a = ensureStarted();
            const before = (state.messages[conversationId] || []).find((m) => m.id === messageId);
            if (!before) return;
            const mine = state.me && before.authorId === state.me.id;
            upsertMessage({ ...before, text: '', attachments: [], card: null, deletedAt: new Date().toISOString(), ...(mine ? {} : { removedBy: state.me && state.me.id }) });
            a.remove(conversationId, messageId, opts).catch(() => { upsertMessage(before); notice("That message couldn't be removed. Try again"); });
        },

        markRead(conversationId) {
            const a = ensureStarted();
            const c = state.conversations[conversationId];
            const me = state.me;
            if (!c || !me || unreadHold === conversationId) return;
            const list = state.messages[conversationId] || [];
            const last = list[list.length - 1];
            const at = last ? last.createdAt : new Date().toISOString();
            if (c.readUpTo && c.readUpTo[me.id] && c.readUpTo[me.id] >= at) return;
            setConversation({ ...c, readUpTo: { ...c.readUpTo, [me.id]: at } });
            a.markRead(conversationId, at).catch(() => { /* the next open marks it again */ });
        },

        /** Back to unread from the latest message someone else sent; it stays unread until reopened. */
        markUnread(conversationId) {
            const c = state.conversations[conversationId];
            const me = state.me;
            if (!c || !me) return;
            const theirs = (state.messages[conversationId] || []).filter((m) => m.authorId !== me.id && !m.system && !m.auto && !m.threadRootId);
            const last = theirs[theirs.length - 1];
            if (!last) return;
            const at = new Date(new Date(last.createdAt).getTime() - 1).toISOString();
            unreadHold = conversationId;
            setConversation({ ...c, readUpTo: { ...c.readUpTo, [me.id]: at } });
            ensureStarted().markUnread(conversationId, at).catch(() => {});
        },

        /** Leaving (or reopening) a chat lets it be read again. */
        releaseUnread(conversationId) {
            if (unreadHold === conversationId) unreadHold = null;
        },

        /** Pin a message to the top of its chat (or unpin it). Everyone in the chat sees it. */
        pinMessage(conversationId, messageId, on) {
            const c = state.conversations[conversationId];
            if (!c) return;
            const ids = (c.pinnedMessageIds || []).filter((x) => x !== messageId).concat(on ? [messageId] : []);
            setConversation({ ...c, pinnedMessageIds: ids });
            ensureStarted().pinMessage(conversationId, messageId, on).catch(() => setConversation(c));
        },

        setTyping(conversationId, on) {
            ensureStarted().setTyping(conversationId, on).catch(() => {});
        },

        markDone(conversationId, done) {
            const c = state.conversations[conversationId];
            if (!c) return;
            // Done means someone handled it: Bailey's handover is answered, and it's read
            const handled = done && needsYou(c) ? { bailey: { ...c.bailey, state: 'paused', takenBy: state.me && state.me.id } } : {};
            setConversation({ ...c, ...handled, doneAt: done ? new Date().toISOString() : null });
            if (done) actions.markRead(conversationId);
            ensureStarted().markDone(conversationId, done).catch(() => { setConversation(c); notice("That didn't save. Try again"); });
        },

        assign(conversationId, staffId) {
            const c = state.conversations[conversationId];
            if (!c) return;
            setConversation({ ...c, assigneeId: staffId || null });
            ensureStarted().assign(conversationId, staffId).catch(() => setConversation(c));
        },

        /** Bailey stops answering this client: a person replies from here (Take over). */
        takeOver(conversationId) {
            const c = state.conversations[conversationId];
            if (!c || !c.bailey) return Promise.resolve(null);
            setConversation({ ...c, bailey: { ...c.bailey, state: 'paused', takenBy: state.me && state.me.id } });
            return ensureStarted().takeOver(conversationId).catch(() => { setConversation(c); notice("That didn't work. Try again"); });
        },

        /** Bailey answers this client again (Hand back). */
        handBack(conversationId) {
            const c = state.conversations[conversationId];
            if (!c || !c.bailey) return Promise.resolve(null);
            setConversation({ ...c, bailey: { ...c.bailey, state: 'replying', takenBy: null } });
            return ensureStarted().handBack(conversationId).catch(() => { setConversation(c); notice("That didn't work. Try again"); });
        },

        resendAuto(conversationId, messageId, channel) {
            return ensureStarted().resendAuto(conversationId, messageId, channel);
        },

        updateConversation(conversationId, patch) {
            const c = state.conversations[conversationId];
            if (!c) return;
            setConversation({ ...c, ...patch });
            ensureStarted().updateConversation(conversationId, patch).catch(() => setConversation(c));
        },

        /** { kind: 'channel' | 'group', name, topic, memberIds } → the new chat, or an error to show. */
        createChat(spec) {
            const a = ensureStarted();
            return loaded.then(() => a.createChat(spec)).then((c) => { setConversation(c); return c; });
        },

        addMembers(conversationId, ids) {
            return ensureStarted().addMembers(conversationId, ids).then((c) => { if (c) setConversation(c); return c; })
                .catch(() => notice("They couldn't be added. Try again"));
        },

        removeMember(conversationId, personId) {
            const c = state.conversations[conversationId];
            if (!c) return Promise.resolve(null);
            setConversation({ ...c, memberIds: c.memberIds.filter((x) => x !== personId) });
            return ensureStarted().removeMember(conversationId, personId).catch(() => { setConversation(c); notice("That didn't work. Try again"); });
        },

        leave(conversationId) {
            const c = state.conversations[conversationId];
            const me = state.me;
            if (!c || !me) return Promise.resolve(null);
            setConversation({ ...c, memberIds: (c.memberIds || []).filter((x) => x !== me.id) });
            return ensureStarted().leave(conversationId).catch(() => { setConversation(c); notice("You couldn't leave just now. Try again"); });
        },

        /** Rename a channel or group, or change a channel's topic. Resolves, or rejects with what to say. */
        editChat(conversationId, patch) {
            const c = state.conversations[conversationId];
            if (!c) return Promise.resolve(null);
            return ensureStarted().updateConversation(conversationId, patch).then((next) => { if (next) setConversation(next); return next; });
        },

        /** { id, name } of a client: their chat, started (and assigned to me) if there isn't one. */
        openClientChat(client) {
            const a = ensureStarted();
            return loaded.then(() => a.openClientChat(client)).then((c) => { setConversation(c); return c; });
        },

        openDm(personId) {
            const a = ensureStarted();
            return loaded.then(() => a.openDm(personId)).then((c) => { setConversation(c); return c; });
        },

        remind(conversationId, messageId, dueAt, label) {
            return ensureStarted().remind(conversationId, messageId, dueAt, label).then((r) => {
                setState((s) => ({ ...s, reminders: s.reminders.concat(r) }));
                return r;
            });
        },

        completeReminder(id) {
            setState((s) => ({ ...s, reminders: s.reminders.map((r) => (r.id === id ? { ...r, done: true } : r)) }));
            ensureStarted().updateReminder(id, { done: true }).catch(() => {});
        },

        snoozeReminder(id, dueAt) {
            setState((s) => ({ ...s, reminders: s.reminders.map((r) => (r.id === id ? { ...r, dueAt, due: false } : r)) }));
            ensureStarted().updateReminder(id, { dueAt, due: false }).catch(() => {});
        },

        toggleSaved(conversationId, messageId) {
            const had = state.saved.some((x) => x.messageId === messageId);
            setState((s) => ({ ...s, saved: had ? s.saved.filter((x) => x.messageId !== messageId) : s.saved.concat({ conversationId, messageId, savedAt: new Date().toISOString() }) }));
            ensureStarted().toggleSaved(conversationId, messageId).catch(() => {});
            return !had;
        },

        /* Beu Support. Bailey and the team answer through events, so these only ask. */

        /** "Did that sort it?" helped: true marks it solved, false hands it to the team. */
        supportFeedback(messageId, helped) {
            return ensureStarted().supportFeedback(messageId, helped).catch(() => notice("That didn't go through. Try again"));
        },

        talkToTeam() {
            return ensureStarted().talkToTeam().catch(() => notice("That didn't go through. Try again"));
        },

        urgentHelp(context) {
            return ensureStarted().urgentHelp(context).catch(() => notice("That didn't go through. Try again"));
        },

        /** How did we do? 'good' closes the ticket, 'bad' reopens it. */
        rateTicket(messageId, rating) {
            return ensureStarted().rateTicket(messageId, rating).catch(() => notice("That didn't go through. Try again"));
        },

        /** Add or change a quick reply (resolves, or rejects with what to say). */
        saveQuickReply(reply) {
            return ensureStarted().saveQuickReply(reply).then((list) => { setState((s) => ({ ...s, quickReplies: list })); return list; });
        },

        deleteQuickReply(id) {
            const before = state.quickReplies;
            setState((s) => ({ ...s, quickReplies: s.quickReplies.filter((r) => r.id !== id) }));
            return ensureStarted().deleteQuickReply(id).catch(() => setState((s) => ({ ...s, quickReplies: before })));
        },

        updatePrefs(patch) {
            const before = state.prefs;
            setState((s) => ({ ...s, prefs: { ...s.prefs, ...patch } }));
            return ensureStarted().updatePrefs(patch).catch(() => { setState((s) => ({ ...s, prefs: before })); notice("That didn't save. Try again"); });
        },

        /** Resolves, or rejects with what to say. */
        updateClientSettings(patch) {
            return ensureStarted().updateClientSettings(patch).then((next) => { setState((s) => ({ ...s, clientSettings: next })); return next; });
        },

        /** All messages, only @mentions, or muted, for one chat. */
        setNotify(conversationId, notify) {
            const c = state.conversations[conversationId];
            if (!c) return;
            const patch = { notify, muted: notify !== 'all' };
            setConversation({ ...c, ...patch });
            ensureStarted().updateConversation(conversationId, patch).catch(() => setConversation(c));
        },

        /** My order for the channels in the rail (drag to reorder). Mine only. */
        reorderChannels(ids) {
            const before = state.channelOrder;
            setState((s) => ({ ...s, channelOrder: ids.slice() }));
            ensureStarted().setChannelOrder(ids).catch(() => setState((s) => ({ ...s, channelOrder: before })));
        },

        /** Take a teammate's booking from their cover request. */
        takeCover(conversationId, messageId) {
            const before = (state.messages[conversationId] || []).find((m) => m.id === messageId);
            if (!before || !before.card || before.card.status !== 'open' || !state.me) return;
            upsertMessage({ ...before, card: { ...before.card, status: 'taken', takenBy: state.me.id } });
            ensureStarted().takeCover(conversationId, messageId).catch(() => { upsertMessage(before); notice('Someone has just taken it'); });
        },

        /** Keep a client's photo on their profile (or take it off). */
        saveToProfile(conversationId, messageId, on) {
            const before = (state.messages[conversationId] || []).find((m) => m.id === messageId);
            if (!before) return;
            upsertMessage({ ...before, savedToProfile: on });
            ensureStarted().saveToProfile(conversationId, messageId, on).catch(() => upsertMessage(before));
        },

        /* The client app: what a client does with a salon's cards. */

        pickSlot(conversationId, messageId, when) {
            return ensureStarted().pickSlot(conversationId, messageId, when).catch(() => notice("That time couldn't be booked. Try again"));
        },

        payDeposit(conversationId, messageId) {
            return ensureStarted().payDeposit(conversationId, messageId).catch(() => notice("The payment didn't go through. Try again"));
        },

        startedForm(conversationId, messageId) {
            ensureStarted().startedForm(conversationId, messageId).catch(() => {});
        },

        submitForm(conversationId, messageId, answers) {
            return ensureStarted().submitForm(conversationId, messageId, answers).catch(() => notice("Your answers didn't send. Try again"));
        },

        /** { id, name } from a booking: resolves to the chat with that salon. */
        openSalon(salon) {
            const a = ensureStarted();
            return loaded.then(() => a.openSalon(salon)).then((c) => { setConversation(c); return c; });
        },

        /** Staging only: put the demo back as it started. */
        /** The chat on screen (Chat mounts and unmounts), so nobody is told about the one they're reading. */
        setActive(conversationId) { activeId = conversationId || null; },

        /** This phone's push token, so the server can send notifications (backend brief, section 29). */
        registerDevice(token, platform) {
            const a = ensureStarted();
            return a.registerDevice ? a.registerDevice(token, platform).catch(() => null) : Promise.resolve(null);
        },

        /** opts.fresh: start as a brand-new salon (the welcome screen and empty spaces). */
        resetDemo(opts) {
            const a = ensureStarted();
            if (a.reset) a.reset(opts);
        },
    };

    return {
        onNotice, onIncoming, ensureStarted, getState, subscribe, useMessagesState,
        selectInbox, selectSpaceUnread, selectTotalUnread, actions,
        /** Tests only. */
        __resetForTests() {
            if (unsubscribe) unsubscribe();
            if (adapter) adapter.dispose();
            adapter = null;
            adapterKey = null;
            unsubscribe = null;
            state = empty();
            listeners.clear();
        },
    };
}

/** The salon's store: Team, Clients and Support. */
export const salonStore = createMessagesStore({ makeAdapter: createAdapter, keyOf: identityKey });

export const {
    onNotice, onIncoming, ensureStarted, getState, subscribe, useMessagesState,
    selectInbox, selectSpaceUnread, selectTotalUnread, actions, __resetForTests,
} = salonStore;

/**
 * Which store the shared chat pieces (the list, the typing box, toasts)
 * talk to. Nothing provides it in the salon app, so they get the salon's.
 */
export const StoreContext = createContext(salonStore);
export const useStore = () => useContext(StoreContext);
